import React, { useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import {
  X,
  Calendar,
  MessageSquare,
  CheckCircle2,
  UserPlus,
  Clock,
  Contact2,
  RefreshCw,
  Building2,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { format, isValid, parseISO } from "date-fns";
import { vi } from "date-fns/locale";

function parseDate(dateStr) {
  if (!dateStr) return null;
  const raw = String(dateStr).trim();
  const iso = parseISO(raw.includes("T") ? raw : `${raw}T00:00:00`);
  if (isValid(iso)) return iso;
  const d = new Date(raw);
  return isValid(d) ? d : null;
}

const SectionTitle = ({ children }) => (
  <h3 className="mb-2.5 flex items-center gap-2 text-[11px] font-black uppercase tracking-widest text-red-500">
    <span className="h-4 w-1 rounded-full bg-red-500" />
    {children}
  </h3>
);

const InfoRow = ({ Icon, label, value, iconClass = "text-gray-400 bg-gray-50" }) => (
  <div className="flex min-w-0 items-center gap-2.5 rounded-xl p-2">
    <div className={`shrink-0 rounded-lg p-2 ${iconClass}`}>
      <Icon className="h-4 w-4" />
    </div>
    <div className="min-w-0">
      <p className="text-[10px] font-bold uppercase tracking-tight text-gray-400">
        {label}
      </p>
      <p className="truncate text-[13px] font-semibold text-gray-800">
        {value || "—"}
      </p>
    </div>
  </div>
);

const ContactHistoryDetailModal = ({
  isOpen,
  onClose,
  contact,
  relatedLogs = [],
  onProgress,
}) => {
  const followupMeta = useMemo(() => {
    if (!contact?.next_contact_date) return null;
    const target = parseDate(contact.next_contact_date);
    if (!target) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const t = new Date(target);
    t.setHours(0, 0, 0, 0);
    const diff = Math.round((t - today) / 86400000);
    if (diff < 0) {
      return {
        label: `Quá hạn ${Math.abs(diff)} ngày`,
        className: "bg-red-100 text-red-800 border-red-200",
      };
    }
    if (diff === 0) {
      return {
        label: "Hẹn hôm nay",
        className: "bg-amber-100 text-amber-900 border-amber-200",
      };
    }
    if (diff === 1) {
      return {
        label: "Hẹn ngày mai",
        className: "bg-orange-50 text-orange-800 border-orange-100",
      };
    }
    const d = parseDate(contact.next_contact_date);
    const short = d ? format(d, "dd/MM/yyyy") : "—";
    return {
      label: `Hẹn ${short}`,
      className: "bg-slate-100 text-slate-700 border-slate-200",
    };
  }, [contact?.next_contact_date]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleEsc = (e) => {
      if (e.key === "Escape") onClose();
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleEsc);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", handleEsc);
    };
  }, [isOpen, onClose]);

  if (!contact || !isOpen) return null;

  const formatDateLong = (dateStr) => {
    const d = parseDate(dateStr);
    if (!d) return dateStr || "—";
    try {
      return format(d, "dd MMMM yyyy", { locale: vi });
    } catch {
      return dateStr;
    }
  };

  const formatDateShort = (dateStr) => {
    const d = parseDate(dateStr);
    if (!d) return "—";
    return format(d, "dd/MM/yyyy");
  };

  const initial = (contact.customer_name || "?").charAt(0).toUpperCase();

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[200] bg-black/45 backdrop-blur-sm"
            onClick={onClose}
          />
          <div className="pointer-events-none fixed inset-0 z-[210] flex items-end justify-center p-0 sm:items-center sm:p-4">
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 30, stiffness: 320 }}
              className="pointer-events-auto flex h-[92dvh] w-full max-w-2xl flex-col overflow-hidden rounded-t-[1.75rem] border border-gray-200 bg-white shadow-2xl sm:h-auto sm:max-h-[90vh] sm:rounded-[2rem]"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex shrink-0 justify-center pt-2 sm:hidden" aria-hidden>
                <span className="h-1 w-10 rounded-full bg-gray-200" />
              </div>

              {/* Header — cùng scale với card danh sách / create modal */}
              <div className="flex shrink-0 items-start justify-between gap-3 border-b border-gray-100 px-4 pb-3 pt-2 sm:items-center sm:px-6 sm:py-4">
                <div className="flex min-w-0 items-center gap-3">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-white bg-gradient-to-br from-red-600 to-rose-700 text-sm font-black text-white shadow-md shadow-red-100 sm:h-12 sm:w-12 sm:text-base">
                    {initial}
                  </div>
                  <div className="min-w-0">
                    <h2 className="truncate text-[15px] font-black text-gray-900 sm:text-lg">
                      {contact.customer_name || "—"}
                    </h2>
                    <p className="mt-0.5 text-[11px] font-medium text-gray-500">
                      Chi tiết thông tin liên hệ
                    </p>
                    {followupMeta ? (
                      <span
                        className={`mt-1.5 inline-flex rounded-full border px-2 py-0.5 text-[10px] font-bold ${followupMeta.className}`}
                      >
                        {followupMeta.label}
                      </span>
                    ) : null}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  className="shrink-0 rounded-xl border border-gray-200 bg-white p-2 text-gray-400 transition hover:border-red-200 hover:bg-red-50 hover:text-red-600"
                  aria-label="Đóng"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="min-h-0 flex-1 space-y-4 overflow-y-auto overscroll-contain px-4 py-4 sm:space-y-5 sm:px-6 sm:py-5">
                {/* Meta — font đồng bộ card list */}
                <div>
                  <SectionTitle>Thông tin liên hệ</SectionTitle>
                  <div className="grid grid-cols-2 gap-x-1 gap-y-0.5 rounded-2xl border border-gray-100 bg-gradient-to-br from-slate-50 via-white to-white p-1.5">
                    <InfoRow
                      Icon={Building2}
                      label="Chi nhánh"
                      value={contact.branch_name}
                      iconClass="bg-blue-50 text-blue-600"
                    />
                    <InfoRow
                      Icon={Contact2}
                      label="Hình thức"
                      value={contact.method}
                      iconClass="bg-purple-50 text-purple-600"
                    />
                    <InfoRow
                      Icon={Calendar}
                      label="Ngày liên hệ"
                      value={formatDateLong(contact.contact_date)}
                      iconClass="bg-red-50 text-red-600"
                    />
                    <InfoRow
                      Icon={UserPlus}
                      label="Nhân viên"
                      value={contact.staff_name}
                      iconClass="bg-emerald-50 text-emerald-600"
                    />
                  </div>
                </div>

                {/* Nội dung */}
                <div>
                  <SectionTitle>Nội dung trao đổi</SectionTitle>
                  <div className="relative overflow-hidden rounded-2xl border border-gray-100 bg-gradient-to-br from-red-50/60 via-white to-rose-50/40 p-3.5">
                    <MessageSquare
                      className="pointer-events-none absolute -right-2 -top-1 h-16 w-16 text-red-100"
                      aria-hidden
                    />
                    <p className="relative text-[13px] leading-relaxed text-gray-700">
                      {contact.content || "Không có nội dung ghi chú."}
                    </p>
                  </div>
                </div>

                {/* Kết quả + hẹn lại */}
                <div className="grid grid-cols-2 gap-2.5">
                  <div className="rounded-2xl border border-emerald-100 bg-gradient-to-br from-emerald-50 via-white to-teal-50/50 p-3">
                    <p className="mb-1.5 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide text-emerald-700">
                      <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
                      Kết quả
                    </p>
                    <p className="text-[13px] font-semibold leading-snug text-gray-800">
                      {contact.result || "Chưa xác định"}
                    </p>
                  </div>
                  <div className="rounded-2xl border border-red-100 bg-gradient-to-br from-red-50 via-white to-orange-50/50 p-3">
                    <p className="mb-1.5 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide text-red-700">
                      <Clock className="h-3.5 w-3.5 shrink-0" />
                      Hẹn lại
                    </p>
                    <p className="text-[13px] font-black leading-snug text-red-700">
                      {contact.next_contact_date
                        ? formatDateShort(contact.next_contact_date)
                        : "Không hẹn"}
                    </p>
                    {followupMeta ? (
                      <p className="mt-1.5 hidden text-[10px] font-bold text-red-600/80 sm:block">
                        {followupMeta.label}
                      </p>
                    ) : null}
                  </div>
                </div>

                {/* Related logs */}
                <div>
                  <SectionTitle>
                    Logs cùng khách ({relatedLogs.length})
                  </SectionTitle>
                  <div className="max-h-44 overflow-auto rounded-2xl border border-gray-100 bg-gray-50/50 sm:max-h-56">
                    {relatedLogs.length === 0 ? (
                      <div className="px-3.5 py-4 text-center text-[13px] text-gray-500">
                        Chưa có logs khác.
                      </div>
                    ) : (
                      relatedLogs.map((log) => {
                        const isCurrent = log.id === contact.id;
                        return (
                          <div
                            key={log.id}
                            className={`flex items-start justify-between gap-2 border-b border-gray-100/80 px-3.5 py-2.5 last:border-b-0 ${
                              isCurrent ? "bg-red-50/70" : ""
                            }`}
                          >
                            <div className="min-w-0">
                              <p className="truncate text-[13px] font-semibold text-gray-800">
                                {log.method || "—"}
                                <span className="font-medium text-gray-400">
                                  {" "}
                                  · {log.staff_name || "—"}
                                </span>
                                {isCurrent ? (
                                  <span className="ml-1.5 rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-bold text-red-700">
                                    Đang xem
                                  </span>
                                ) : null}
                              </p>
                              <p className="mt-0.5 line-clamp-2 text-[11px] leading-snug text-gray-500">
                                {log.content || "—"}
                              </p>
                            </div>
                            <div className="shrink-0 pt-0.5 text-[11px] font-bold tabular-nums text-gray-500">
                              {formatDateShort(log.contact_date)}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>

              <div
                className="flex shrink-0 items-center gap-2 border-t border-gray-100 bg-white px-4 pt-3 sm:justify-end sm:gap-3 sm:px-6"
                style={{
                  paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))",
                }}
              >
                <button
                  type="button"
                  onClick={onClose}
                  className={`min-h-[44px] flex-1 rounded-xl px-4 py-2.5 text-xs font-bold transition active:scale-[0.98] sm:flex-none sm:rounded-full sm:px-6 ${
                    onProgress
                      ? "border border-gray-200 bg-white text-gray-600"
                      : "bg-slate-900 text-white"
                  }`}
                >
                  Đóng
                </button>
                {onProgress ? (
                  <button
                    type="button"
                    onClick={onProgress}
                    className="flex min-h-[44px] flex-[1.35] items-center justify-center gap-1.5 rounded-xl bg-gradient-to-r from-red-700 to-rose-700 px-4 py-2.5 text-xs font-bold text-white shadow-sm shadow-red-100 active:scale-[0.98] sm:flex-none sm:rounded-full sm:px-6"
                  >
                    <RefreshCw className="h-3.5 w-3.5 shrink-0" />
                    <span className="truncate">Cập nhật tiến độ</span>
                  </button>
                ) : null}
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default ContactHistoryDetailModal;
